import { Tooltip } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import moment from "moment";
import styled, { createGlobalStyle } from "styled-components";

import { IWorkspaceResource, IWorkspaceResourceFolder } from "common/interfaces";
import { Icon } from "js/Components/Icon";
import { Menu, MenuItem } from "js/Components/Menu";
import getLogger, { LogGroup } from "js/core/logger";
import getUserProfile from "js/core/services/userProfiles";
import { LibraryBreadcrumb } from "js/editor/PresentationLibrary/components/LibraryBreadcrumb";
import { TruncatedTooltip } from "js/editor/PresentationLibrary/components/TruncatedTooltip";
import { FolderPathInfo, getFolderPathsForPresentation } from "js/editor/PresentationLibrary/utils/folderPaths";
import { t } from "js/i18n";

const logger = getLogger(LogGroup.WORKSPACES);

const FOLDER_TOOLTIP_CLASS = "shared-resource-folder-tooltip";
const CONTEXT_TOOLTIP_CLASS = "shared-resource-context-tooltip";

const ContextTooltipStyles = createGlobalStyle`
    .${CONTEXT_TOOLTIP_CLASS}.MuiTooltip-tooltip {
        font-size: 12px;
        font-weight: 400;
        padding: 8px;
        color: white;
        max-width: 200px;
        line-height: 125%;
        border-radius: 4px;
        background: rgba(80, 80, 80, 0.95);
        box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.20);
    }

    .${FOLDER_TOOLTIP_CLASS}.MuiTooltip-tooltip {
        padding: 8px 12px;
        justify-content: center;
        align-items: center;
        gap: 10px;
        box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.20);
        max-width: min(600px, 90vw);
        width: max-content;
    }
`;

const ContextRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: auto;
    max-width: 100%;
    min-width: 0;
    min-height: 20px;
    overflow: hidden;
    color: #666;
    font-size: 11px;
    line-height: 16px;
`;

const ContextLabel = styled.div`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ActivityLabel = styled(ContextLabel)`
    flex: 0 1 auto;
`;

const ActivityLabelText = styled.span`
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ContextDivider = styled.div`
    width: 1px;
    height: 12px;
    flex-shrink: 0;
    border-radius: 100px;
    background: rgba(0, 0, 0, 0.1);
`;

const FolderContextWrap = styled.div<{ $maxWidth?: string }>`
    min-width: 0;
    flex: 0 1 auto;
    max-width: ${props => props.$maxWidth ?? "160px"};
`;

const FolderTrigger = styled.div<{ $clickable: boolean }>`
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    overflow: hidden;
    cursor: ${props => props.$clickable ? "pointer" : "default"};
    text-transform: capitalize;
    user-select: none;

    &:hover .folder-path-text {
        color: ${props => props.$clickable ? "#11A9E2" : "inherit"};
        text-decoration: ${props => props.$clickable ? "underline" : "none"};
        text-decoration-color: #11A9E2;
    }
`;

const FolderPathLabel = styled.span`
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const FolderMenu = styled(Menu)`
    .MuiPaper-root {
        border: none;
        border-radius: 2px;
        box-shadow: 0px 0px 50px rgb(0 0 0 / 25%);
    }

    .MuiList-padding {
        padding-top: 4px;
        padding-bottom: 4px;
        overflow: hidden;
        background: #fff;
    }
`;

const FolderPathItem = styled(MenuItem)`
    &&& {
        min-width: 180px;
        max-width: 320px;
        padding: 8px 12px;
    }
`;

const FolderPathItemContent = styled.div`
    text-transform: capitalize;
    display: flex;
    align-items: center;
    gap: 6px;
    color: #333;
    width: 100%;
    min-width: 0;
`;

export type SharedResourceFolderNavigateHandler = (folderPath: FolderPathInfo) => void;

type SharedResourceMetadataWithActivity = {
    contentModifiedAt?: number;
    createdBy?: string;
    modifiedBy?: string;
};

type SharedResourceContextListProps = {
    activityLabel?: React.ReactNode;
    activityTooltip?: React.ReactNode;
    activityTooltipClasses?: { tooltip?: string };
    className?: string;
    folderMaxWidth?: string;
    resource?: IWorkspaceResource | null;
    resourceFolders?: IWorkspaceResourceFolder[];
    onNavigateToFolder?: SharedResourceFolderNavigateHandler;
};

type SharedResourceActivityInfo = {
    createdAt?: number;
    createdBy?: string;
    modifiedAt?: number;
    modifiedBy?: string;
};

export function getSharedResourceActivityInfo(resource: IWorkspaceResource): SharedResourceActivityInfo {
    const metadata = resource.metadata as SharedResourceMetadataWithActivity;

    return {
        createdAt: resource.createdAt,
        createdBy: metadata.createdBy,
        modifiedAt: metadata.contentModifiedAt ?? resource.modifiedAt,
        modifiedBy: metadata.modifiedBy,
    };
}

export async function getSharedResourceActivityLabel({
    createdAt,
    createdBy,
    modifiedAt,
    modifiedBy,
}: SharedResourceActivityInfo): Promise<string | null> {
    if (!createdAt || !createdBy) {
        return null;
    }

    const showModified = !!modifiedBy && !!modifiedAt && modifiedAt > createdAt;
    const [createdProfile, modifiedProfile] = await Promise.all([
        getUserProfile(createdBy),
        showModified ? getUserProfile(modifiedBy) : Promise.resolve(null),
    ]);
    const user = showModified ? modifiedProfile : createdProfile;
    const actionLabel = showModified ? t("Edited by") : t("Created by");
    const time = showModified ? modifiedAt : createdAt;

    return `${actionLabel} ${user?.displayName || user?.email || t("Unknown")}, ${moment(time).fromNow()}`;
}

export function useSharedResourceActivityLabel(resource?: IWorkspaceResource | null) {
    const [activityLabel, setActivityLabel] = useState<string | null>(null);
    const metadata = resource?.metadata as SharedResourceMetadataWithActivity | undefined;

    useEffect(() => {
        let isMounted = true;

        if (!resource) {
            setActivityLabel(null);
            return () => {
                isMounted = false;
            };
        }

        getSharedResourceActivityLabel(getSharedResourceActivityInfo(resource))
            .then(label => {
                if (isMounted) {
                    setActivityLabel(label);
                }
            })
            .catch(err => {
                logger.error(err, "[SharedResourceContextList] getSharedResourceActivityLabel failed", { id: resource.resourceId });
                if (isMounted) {
                    setActivityLabel(null);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [
        resource?.resourceId,
        resource?.createdAt,
        resource?.modifiedAt,
        metadata?.createdBy,
        metadata?.modifiedBy,
        metadata?.contentModifiedAt,
    ]);

    return activityLabel;
}

function SharedResourceFolderContext({
    folderMaxWidth,
    resource,
    resourceFolders,
    onNavigateToFolder,
}: {
    folderMaxWidth?: string;
    resource: IWorkspaceResource;
    resourceFolders: IWorkspaceResourceFolder[];
    onNavigateToFolder?: SharedResourceFolderNavigateHandler;
}) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const folderPaths = getFolderPathsForPresentation(resource, resourceFolders) ?? [];

    if (!folderPaths.length) {
        return null;
    }

    const isSingleFolder = folderPaths.length === 1;
    const clickable = !!onNavigateToFolder;
    const label = isSingleFolder ? folderPaths[0].displayLabel : t("{{count}} folders", { count: folderPaths.length });
    const handleNavigate = (folderPath: FolderPathInfo) => {
        onNavigateToFolder?.(folderPath);
        setAnchorEl(null);
    };

    return (
        <FolderContextWrap $maxWidth={folderMaxWidth}>
            <FolderTrigger
                $clickable={clickable}
                onMouseDown={event => event.stopPropagation()}
                onClick={event => {
                    event.stopPropagation();
                    if (!clickable) {
                        return;
                    }
                    if (isSingleFolder) {
                        handleNavigate(folderPaths[0]);
                    } else {
                        setAnchorEl(anchorEl ? null : event.currentTarget);
                    }
                }}
            >
                <Icon size={16}>folder</Icon>
                <TruncatedTooltip
                    title={isSingleFolder ? <LibraryBreadcrumb pathParts={folderPaths[0].pathParts} /> : label}
                    tooltipClasses={{ tooltip: FOLDER_TOOLTIP_CLASS }}
                    enterDelay={500}
                    enterNextDelay={500}
                >
                    <FolderPathLabel className="folder-path-text">{label}</FolderPathLabel>
                </TruncatedTooltip>
            </FolderTrigger>

            {!isSingleFolder && clickable && (
                <FolderMenu
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    onClose={() => setAnchorEl(null)}
                    getContentAnchorEl={null}
                    onClick={event => event.stopPropagation()}
                >
                    {folderPaths.map(info => (
                        <FolderPathItem
                            key={info.folderId}
                            onClick={event => {
                                event.stopPropagation();
                                handleNavigate(info);
                            }}
                            onMouseDown={event => event.stopPropagation()}
                        >
                            <FolderPathItemContent>
                                <Icon size={18}>folder</Icon>
                                <TruncatedTooltip
                                    title={<LibraryBreadcrumb pathParts={info.pathParts} />}
                                    tooltipClasses={{ tooltip: FOLDER_TOOLTIP_CLASS }}
                                    enterDelay={500}
                                    enterNextDelay={500}
                                >
                                    <FolderPathLabel>
                                        <LibraryBreadcrumb pathParts={info.pathParts} iconColor="#666" />
                                    </FolderPathLabel>
                                </TruncatedTooltip>
                            </FolderPathItemContent>
                        </FolderPathItem>
                    ))}
                </FolderMenu>
            )}
        </FolderContextWrap>
    );
}

export function SharedResourceContextList({
    activityLabel,
    activityTooltip,
    activityTooltipClasses,
    className,
    folderMaxWidth,
    resource,
    resourceFolders = [],
    onNavigateToFolder,
}: SharedResourceContextListProps) {
    const hasFolderContext = !!resource && resource.folderIds?.some(folderId => resourceFolders.some(folder => folder.id === folderId));
    const items: React.ReactNode[] = [];

    if (activityLabel) {
        items.push(
            <ActivityLabel key="activity">
                <TruncatedTooltip
                    title={activityTooltip ?? activityLabel}
                    tooltipClasses={activityTooltipClasses ?? { tooltip: CONTEXT_TOOLTIP_CLASS }}
                    enterDelay={500}
                    enterNextDelay={500}
                >
                    <ActivityLabelText>{activityLabel}</ActivityLabelText>
                </TruncatedTooltip>
            </ActivityLabel>
        );
    }

    if (hasFolderContext && resource) {
        items.push(
            <SharedResourceFolderContext
                key="folder"
                folderMaxWidth={folderMaxWidth}
                resource={resource}
                resourceFolders={resourceFolders}
                onNavigateToFolder={onNavigateToFolder}
            />
        );
    }

    if (!items.length) {
        return null;
    }

    return (
        <ContextRow className={className}>
            <ContextTooltipStyles />
            {items.map((item, index) => (
                <React.Fragment key={index}>
                    {index > 0 && <ContextDivider />}
                    {item}
                </React.Fragment>
            ))}
        </ContextRow>
    );
}
